import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';

function OESEditSidebar({
  isOpen,
  onClose,
  oes,
  onOESUpdated,
  assessments = [],
  defaultAssessmentId = '',
  assessmentLocked = false,
}) {
  const labels = useLabels();
  const t = useT();
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    description: '',
    sector_list_id: null,
    sector_id: null,
    assessment_id: defaultAssessmentId || '',
    legal_name: '',
    registration_number: '',
    address: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    contact_person_name: '',
    contact_person_title: '',
    contact_person_email: '',
    contact_person_phone: '',
    owner_id: 1, // Default to admin
  });
  const [sectorLists, setSectorLists] = useState([]);
  const [sectorListSectors, setSectorListSectors] = useState([]);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSectorLists, setLoadingSectorLists] = useState(false);
  const [error, setError] = useState(null);
  const userRole = localStorage.getItem('role') || 'user';
  const isGlobalAdmin = userRole === 'global_admin';
  const isOrgAdmin = userRole === 'admin';

  // Load sector lists on component mount
  useEffect(() => {
    const loadSectorLists = async () => {
      setLoadingSectorLists(true);
      try {
        const res = await adminApi.getSectorLists();
        setSectorLists(res.data || []);
      } catch (err) {
        console.error('Failed to load sector lists:', err);
      } finally {
        setLoadingSectorLists(false);
      }
    };

    if (isOpen) {
      loadSectorLists();
    }
  }, [isOpen]);

  // When sector list changes, update available sectors
  useEffect(() => {
    if (formData.sector_list_id) {
      const selectedList = sectorLists.find(sl => sl.id === parseInt(formData.sector_list_id, 10));
      if (selectedList && selectedList.sectors) {
        setSectorListSectors(selectedList.sectors);
        // Only reset sector_id if user is changing the list AND they had previously selected a sector from a different list
        // Don't reset during initial load of an existing OES
        if (oes && oes.sector_list_id === formData.sector_list_id && formData.sector_id) {
          // This is edit mode with same list, keep the sector_id
          return;
        }
        // Only reset if user is switching between lists (create mode or list changed)
        if (!oes || oes.sector_list_id !== formData.sector_list_id) {
          setFormData(prev => ({ ...prev, sector_id: null }));
        }
      }
    } else {
      setSectorListSectors([]);
    }
  }, [formData.sector_list_id, sectorLists, oes]);

  const sectors = [];

  const countries = [
    { code: '', name: 'Select country' },
    { code: 'AF', name: 'Afghanistan' },
    { code: 'AL', name: 'Albania' },
    { code: 'DZ', name: 'Algeria' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'AD', name: 'Andorra' },
    { code: 'AO', name: 'Angola' },
    { code: 'AI', name: 'Anguilla' },
    { code: 'AQ', name: 'Antarctica' },
    { code: 'AG', name: 'Antigua and Barbuda' },
    { code: 'AR', name: 'Argentina' },
    { code: 'AM', name: 'Armenia' },
    { code: 'AW', name: 'Aruba' },
    { code: 'AU', name: 'Australia' },
    { code: 'AT', name: 'Austria' },
    { code: 'AZ', name: 'Azerbaijan' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'BB', name: 'Barbados' },
    { code: 'BY', name: 'Belarus' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BZ', name: 'Belize' },
    { code: 'BJ', name: 'Benin' },
    { code: 'BM', name: 'Bermuda' },
    { code: 'BT', name: 'Bhutan' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'BW', name: 'Botswana' },
    { code: 'BR', name: 'Brazil' },
    { code: 'BN', name: 'Brunei' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'BF', name: 'Burkina Faso' },
    { code: 'BI', name: 'Burundi' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'CM', name: 'Cameroon' },
    { code: 'CA', name: 'Canada' },
    { code: 'CV', name: 'Cape Verde' },
    { code: 'KY', name: 'Cayman Islands' },
    { code: 'CF', name: 'Central African Republic' },
    { code: 'TD', name: 'Chad' },
    { code: 'CL', name: 'Chile' },
    { code: 'CN', name: 'China' },
    { code: 'CO', name: 'Colombia' },
    { code: 'KM', name: 'Comoros' },
    { code: 'CG', name: 'Congo' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'HR', name: 'Croatia' },
    { code: 'CU', name: 'Cuba' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'DK', name: 'Denmark' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'EG', name: 'Egypt' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'EE', name: 'Estonia' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'GR', name: 'Greece' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'HT', name: 'Haiti' },
    { code: 'HN', name: 'Honduras' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IS', name: 'Iceland' },
    { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IL', name: 'Israel' },
    { code: 'IT', name: 'Italy' },
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'Korea (South)' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MX', name: 'Mexico' },
    { code: 'MA', name: 'Morocco' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'NO', name: 'Norway' },
    { code: 'PA', name: 'Panama' },
    { code: 'PE', name: 'Peru' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'RO', name: 'Romania' },
    { code: 'RU', name: 'Russian Federation' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SG', name: 'Singapore' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'ES', name: 'Spain' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'TR', name: 'Turkey' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'VN', name: 'Vietnam' },
  ];

  // Reset form when oes prop changes or sidebar opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Sidebar closed - reset everything
      setActiveTab('basic');
      setFormData({
        name: '',
        short_name: '',
        description: '',
        sector_list_id: null,
        sector_id: null,
        assessment_id: defaultAssessmentId || '',
        legal_name: '',
        registration_number: '',
        address: '',
        city: '',
        country: '',
        phone: '',
        email: '',
        website: '',
        contact_person_name: '',
        contact_person_title: '',
        contact_person_email: '',
        contact_person_phone: '',
        owner_id: 1,
      });
      setLogo(null);
      setLogoPreview(null);
      setError(null);
      return;
    }

    // Sidebar opened - populate form based on oes prop
    if (oes) {
      // Edit mode - populate with OES data
      setFormData({
        name: oes.name || '',
        short_name: oes.short_name || '',
        description: oes.description || '',
        assessment_id: oes.assessment_id || defaultAssessmentId || '',
        legal_name: oes.legal_name || '',
        registration_number: oes.registration_number || '',
        address: oes.address || '',
        city: oes.city || '',
        country: oes.country || '',
        phone: oes.phone || '',
        email: oes.email || '',
        website: oes.website || '',
        contact_person_name: oes.contact_person_name || '',
        contact_person_title: oes.contact_person_title || '',
        contact_person_email: oes.contact_person_email || '',
        contact_person_phone: oes.contact_person_phone || '',
        sector_list_id: oes.sector_list_id ? parseInt(oes.sector_list_id, 10) : null,
        sector_id: oes.sector_id ? parseInt(oes.sector_id, 10) : null,
        owner_id: oes.owner_id || 1,
      });
      if (oes.logo) {
        setLogoPreview(oes.logo);
      } else {
        setLogoPreview(null);
      }
      setLogo(null); // Clear file input when editing
    } else {
      // Create mode - clear everything
      setFormData({
        name: '',
        short_name: '',
        description: '',
        sector_list_id: null,
        sector_id: null,
        assessment_id: defaultAssessmentId || '',
        legal_name: '',
        registration_number: '',
        address: '',
        city: '',
        country: '',
        phone: '',
        email: '',
        website: '',
        contact_person_name: '',
        contact_person_title: '',
        contact_person_email: '',
        contact_person_phone: '',
        owner_id: 1,
      });
      setLogo(null);
      setLogoPreview(null);
    }
  }, [isOpen, oes, defaultAssessmentId]);

  useEffect(() => {
    if (isOpen && !oes && defaultAssessmentId) {
      setFormData(prev => ({ ...prev, assessment_id: defaultAssessmentId }));
    }
  }, [isOpen, defaultAssessmentId, oes]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate sector list and sector are both selected
    if (!formData.sector_list_id || !formData.sector_id) {
      setError('Please select both a sector list and a sector');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        assessment_id: formData.assessment_id ? parseInt(formData.assessment_id, 10) : null,
        sector_list_id: formData.sector_list_id ? parseInt(formData.sector_list_id, 10) : null,
        sector_id: formData.sector_id ? parseInt(formData.sector_id, 10) : null,
      };
      if (oes) {
        // Update existing OES
        await adminApi.updateOES(oes.id, payload);
        if (logo) {
          await adminApi.uploadOESLogo(oes.id, logo);
        }
      } else {
        // Create new OES
        const response = await adminApi.createOES(payload);
        if (logo && response.data.id) {
          await adminApi.uploadOESLogo(response.data.id, logo);
        }
      }

      if (onOESUpdated) {
        onOESUpdated();
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to save ${labels.entityName}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900 bg-opacity-30 z-40 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Sidebar */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-3xl bg-white shadow-xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-eucnet-navy">
              {oes ? t('entity.editEntity', { entity: labels.entityName, name: oes.name }) : t('entity.createNew', { entity: labels.entityName })}
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            >
              <span className="text-xl leading-none">&times;</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-4 mt-4">
            <button
              onClick={() => setActiveTab('basic')}
              className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'basic'
                  ? 'border-eucnet-blue text-eucnet-blue'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('entity.basicInfo')}
            </button>
            <button
              onClick={() => setActiveTab('contact')}
              className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'contact'
                  ? 'border-eucnet-blue text-eucnet-blue'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('entity.contactInfo')}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
              {error}
            </div>
          )}

          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('entity.organizationLogo')}</label>
                <div className="flex items-center space-x-4">
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-200 flex items-center justify-center">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-2" />
                    ) : (
                      <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      id="logo"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="logo"
                      className="cursor-pointer px-4 py-2 bg-eucnet-blue text-white rounded-lg hover:bg-eucnet-navy transition-colors inline-block"
                    >
                      {t('entity.chooseLogo')}
                    </label>
                    <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 2MB</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('entity.organizationName')} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Assessment {isOrgAdmin ? '*' : isGlobalAdmin ? '' : '*'}
                  </label>
                  <CustomSelect
                    name="assessment_id"
                    value={formData.assessment_id}
                    onChange={handleInputChange}
                    options={assessmentLocked 
                      ? assessments.map(a => ({ value: a.id, label: a.name }))
                      : [{ value: '', label: 'Select assessment' }, ...assessments.map(a => ({ value: a.id, label: a.name }))]
                    }
                    placeholder="Select assessment"
                    disabled={assessmentLocked}
                    required={isOrgAdmin}
                  />
                  {isGlobalAdmin && !formData.assessment_id && (
                    <p className="mt-1 text-xs text-slate-500">
                      If not selected, will be assigned to Default Assessment
                    </p>
                  )}
                  {isOrgAdmin && !formData.assessment_id && (
                    <p className="mt-1 text-xs text-slate-500">
                      Assessment is required. Please select an assessment from your organization.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('entity.shortName')}</label>
                  <input
                    type="text"
                    name="short_name"
                    value={formData.short_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('entity.sectorList')} *</label>
                  <CustomSelect
                    name="sector_list_id"
                    value={formData.sector_list_id || ''}
                    onChange={(e) => {
                      const val = e.target.value ? parseInt(e.target.value, 10) : null;
                      setFormData(prev => ({ ...prev, sector_list_id: val, sector_id: null }));
                    }}
                    options={[
                      { value: '', label: t('entity.selectSectorList') },
                      ...sectorLists.map(sl => ({ value: String(sl.id), label: `${sl.name}${sl.is_predefined ? ' (Predefined)' : ''}` }))
                    ]}
                    placeholder={t('entity.selectSectorListPlaceholder')}
                    disabled={loadingSectorLists}
                    required
                  />
                </div>
                {formData.sector_list_id && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('entity.sector')} *</label>
                    <CustomSelect
                      name="sector_id"
                      value={formData.sector_id || ''}
                      onChange={(e) => {
                        const val = e.target.value ? parseInt(e.target.value, 10) : null;
                        setFormData(prev => ({ ...prev, sector_id: val }));
                      }}
                      options={[
                        { value: '', label: t('entity.selectSector') },
                        ...sectorListSectors.map(s => ({ 
                          value: String(s.id), 
                          label: `${s.name}${s.nis2_category ? ` (${s.nis2_category})` : ''}` 
                        }))
                      ]}
                      placeholder={t('entity.selectSector')}
                      required
                    />
                  </div>
                )}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('common.description')}</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('entity.legalName')}</label>
                  <input
                    type="text"
                    name="legal_name"
                    value={formData.legal_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('entity.registrationNumber')}</label>
                  <input
                    type="text"
                    name="registration_number"
                    value={formData.registration_number}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('entity.address')}</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('entity.city')}</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('entity.country')}</label>
                  <CustomSelect
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    options={countries.map(c => ({ value: c.code, label: c.name }))}
                    placeholder="Select country"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Contact Information Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                />
              </div>

              <div className="border-t border-slate-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('entity.contactPerson')}</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                    <input
                      type="text"
                      name="contact_person_name"
                      value={formData.contact_person_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                    <input
                      type="text"
                      name="contact_person_title"
                      value={formData.contact_person_title}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="contact_person_email"
                      value={formData.contact_person_email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      name="contact_person_phone"
                      value={formData.contact_person_phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-slate-200 mt-8">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium bg-eucnet-sky text-white rounded-lg hover:bg-eucnet-navy transition-colors disabled:opacity-50"
            >
              {loading ? t('common.saving') : oes ? t('settings.saveChanges') : t('entity.create', { entity: labels.entityName })}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default OESEditSidebar;
